<script setup lang="ts">
import { computed, reactive, watch } from "vue";
import DRDialog from "../UI/feedbacks/DRDialog.vue";
import { useValidation } from "@/composable/useValidation";
import { ProjectStatus } from "@/types/projects.types";
import DRColorPicker from "../UI/form/DRColorPicker.vue";
import ProjectAvatar from "./ProjectAvatar.vue";
import { useScreen } from "@/composable/useResponsive";

const defaultColor = "#000000";
const props = withDefaults(
  defineProps<{
    modelValue: boolean;
    nameOnly?: boolean;
    project?: {
      name: string;
      color: string;
      status: ProjectStatus;
    };
    loading?: boolean;
  }>(),
  {
    loading: false,
    nameOnly: false,
  }
);

const form = reactive({
  name: "",
  color: defaultColor,
});

const { check, getMessage } = useValidation(form, ({ required }) => ({
  name: {
    required,
  },
  color: {
    required,
  },
}));

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "cancel"): void;
  (
    e: "confirm",
    payload: {
      name: string;
      color: string;
    }
  ): void;
}>();

const show = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit("update:modelValue", value),
});

watch(
  show,
  (value) => {
    if (!value) return;
    form.name = props.project?.name ?? "";
    form.color = props.project?.color ?? defaultColor;
  },
  { immediate: true }
);

const cancel = () => {
  emit("cancel");
  show.value = false;
};

const confirm = () => {
  const isValid = check();
  if (!isValid) return;
  emit("confirm", {
    name: form.name,
    color: form.color,
  });
};

const { screen } = useScreen();
</script>

<template>
  <DRDialog
    v-model="show"
    :title="
      project
        ? nameOnly
          ? $t('dashboard:projects.dialog.rename-project-title')
          : $t('dashboard:projects.dialog.update-project-title')
        : $t('dashboard:projects.dialog.new-project-title')
    "
    @cancel="cancel"
    @confirm="confirm"
    :loading="loading"
    :width="screen.gt.md ? '500px' : '90%'"
  >
    <div class="d-flex gap-2">
      <DRColorPicker v-if="!nameOnly" v-model="form.color">
        <template #reference="{ currentColor }">
          <ProjectAvatar
            class="cursor-pointer"
            :project="{ color: currentColor }"
          ></ProjectAvatar>
        </template>
      </DRColorPicker>
      <el-input
        v-model="form.name"
        type="text"
        id="name"
        size="large"
        :placeholder="$t('dashboard:projects.dialog.form.name.placeholder')"
        :error="getMessage('name')"
      />
    </div>
  </DRDialog>
</template>

<style lang="scss" scoped></style>
